/**
 * MentionNode — Custom TipTap-Node für @-Mentions zu Adressen / Projekten.
 *
 * Vorteile gegenüber Link:
 *  - atomare Einheit (lässt sich mit Backspace komplett löschen, nicht zeichenweise)
 *  - eigener Click-Handler (öffnet den verknüpften Datensatz)
 *  - eigene Render-Logik (Pill-Style, kein blauer Link-Underline)
 *  - kontrolliertes Copy/Paste
 *
 * HTML-Output (kompatibel mit Backend `_extract_mentions` Regex):
 *   <span class="mention" data-type="address" data-id="UUID" data-label="Name">@Name</span>
 *
 * ParseHTML akzeptiert auch das Legacy-Format `<a class="mention" ...>` für
 * Rückwärtskompatibilität mit bereits gespeicherten Notes.
 */
import { Node, mergeAttributes } from '@tiptap/core';

export const MentionNode = Node.create({
  name: 'mention',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addOptions() {
    return {
      onClick: null, // (item) => void; wird vom Editor-Wrapper gesetzt
      HTMLAttributes: {},
    };
  },

  addAttributes() {
    return {
      type: {
        default: 'address',
        parseHTML: (el) => el.getAttribute('data-type'),
        renderHTML: (attrs) => ({ 'data-type': attrs.type }),
      },
      mentionId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-id'),
        renderHTML: (attrs) => ({ 'data-id': attrs.mentionId }),
      },
      label: {
        default: '',
        parseHTML: (el) => el.getAttribute('data-label') || (el.textContent || '').replace(/^@/, ''),
        renderHTML: (attrs) => ({ 'data-label': attrs.label }),
      },
    };
  },

  parseHTML() {
    return [
      // Neues Format
      { tag: 'span.mention[data-id]' },
      // Legacy-Format (alte Notes mit Link-Mention)
      {
        tag: 'a.mention[data-id]',
        getAttrs: (el) => {
          if (!(el instanceof HTMLElement)) return false;
          return {
            type: el.getAttribute('data-type') || 'address',
            mentionId: el.getAttribute('data-id'),
            label: el.getAttribute('data-label') || (el.textContent || '').replace(/^@/, ''),
          };
        },
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const type = HTMLAttributes['data-type'] || 'address';
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        class: `mention mention-${type}`,
        contenteditable: 'false',
      }),
      `@${HTMLAttributes['data-label'] || ''}`,
    ];
  },

  addCommands() {
    return {
      insertMention:
        (attrs) =>
        ({ chain }) => {
          // Wenn schon @ + query getippt wurde, Aufrufer löscht den Range vorab.
          return chain()
            .focus()
            .insertContent([
              { type: this.name, attrs },
              { type: 'text', text: ' ' },
            ])
            .run();
        },
    };
  },

  addKeyboardShortcuts() {
    return {
      // Backspace direkt nach einer Mention → Mention als Atom löschen (Standard-Verhalten),
      // dies wird durch atom: true ohnehin geleistet – kein Custom-Handling nötig.
    };
  },
});

export default MentionNode;
